import type { OfferKind } from '@revelio/core/global-ai-assistant/offer-marker';
import type { TalentDiscoveryAiFilterSearchResponseV2 } from '@revelio/data-access';

import {
  BREAKDOWN_ANSWER_COMPANIONS,
  BREAKDOWN_ANSWER_CONFIGS,
} from './breakdown-answer-configs';
import {
  DIRECT_ANSWER_COMPANIONS,
  DIRECT_ANSWER_CONFIGS,
} from './direct-answer-configs';
import { toStandardPageLabel } from './global-ai-assistant-standard-page-match';
import {
  evalRecordDigest,
  evalRecordToolCall,
  evalRecordToolResult,
  evalRecordToolThrow,
} from './global-ai-eval-recorder';
import type { ToolEmit } from './global-ai-tool-progress';
import type { ArtifactCache } from './global-ai-turn-messages';
import { stripOfferMarkers } from './offer-marker';
import type {
  ChartDataDigest,
  FilterResolutionDigest,
} from './turn-outcome-digest';
import { buildFilterResolutionDigest } from './turn-outcome-digest';

/**
 * Gate a grounded-offer artifact on having actually RESOLVED something. The
 * resolver can return "successfully empty" — an expr with no children and the
 * named entity in `unknownFilters` (observed live when the company search
 * cannot match the name). Applying that empty set on the destination would
 * REPLACE the page's default filters with nothing; pure navigation is strictly
 * better, so an empty artifact is treated as no artifact. An artifact whose
 * shape isn't the TD v2 response passes through unchanged (we can't judge it).
 */
function groundedArtifactOrUndefined(artifact: unknown): unknown {
  if (
    typeof artifact !== 'object' ||
    artifact === null ||
    !('relevantPromptText' in artifact) ||
    !('filters' in artifact)
  ) {
    return artifact ?? undefined;
  }
  const response = artifact as TalentDiscoveryAiFilterSearchResponseV2;
  return buildFilterResolutionDigest({ response, targetPage: '' }).applied
    .length > 0
    ? artifact
    : undefined;
}

export type ClientToolDeps = {
  /**
   * Request a NAVIGATION CONFIRMATION instead of navigating. The hard rule is
   * that the assistant never navigates without an explicit user click, so the
   * `navigate` tool is terminal: it appends a confirmation chip and ends the
   * turn. Resolves the optionId to a destination page name, or reports it is
   * not a known page so the model doesn't claim a phantom navigation.
   */
  requestNavigateConfirm: (input: {
    optionId: string;
    /**
     * Whether the nav-confirm chip resumes the turn (replaying the user's
     * prompt) after landing. Default true — a plain `navigate` may still have a
     * filter/task to finish on the destination. `offer_page_or_custom` sets this
     * false: its page chip is a terminal "just land me on the page" action (the
     * analytical/causal follow-up is the separate build option), so replaying
     * the (often causal) prompt would make the resumed turn try to answer it by
     * filtering the page — an unwanted loop.
     */
    withContinuation?: boolean;
    /**
     * Resolved search_filters artifact for a DATA-GROUNDED offer: the click
     * applies this exact artifact on the destination (deterministic text=link
     * — the page shows the same slice the narrated figures came from). Passed
     * by `offer_page_or_custom` when the model supplies `filterArtifactId`;
     * absent → the click stays pure navigation (existing behavior).
     */
    resolvedFilters?: unknown;
    /** Entity anchor for `resolvedFilters`. */
    entityType?: string;
  }) => { pageName: string; messageId: string } | { unknownPage: true };
  applyFilters: (input: {
    filterResponse: unknown;
    target: string;
    merge?: boolean;
  }) => Promise<FilterResolutionDigest>;
  renderChart: (input: {
    config: unknown;
    filters?: unknown;
    entityType?: string;
    intentSummary?: string;
    chartSubtitle?: string;
    standardPagePath?: string;
  }) => Promise<ChartDataDigest>;
  /**
   * Headless single-value digest fetch (DP3) — offscreen digest host, no
   * chart UI, no transcript message. See ClientToolHandles.fetchChartDigest.
   */
  fetchChartDigest: (input: {
    config: unknown;
    filters?: unknown;
    entityType?: string;
    intentSummary?: string;
    chartSubtitle?: string;
  }) => Promise<ChartDataDigest>;
  /** Append the card-less offer:page context message for a successful answer. */
  appendAnswerContextChart: (input: {
    config: unknown;
    filters?: unknown;
    entityType?: string;
    intentSummary?: string;
    standardPagePath: string;
  }) => void;
  requestCreateCustomPage: (
    input: {
      prompt: string;
      entityType: string;
      /**
       * Force OFFER mode: always append the build-confirm chip, never BUILD-HERE,
       * regardless of the pending-build ref (and clear that ref if stale). Used
       * by `offer_page_or_custom`, which is a pure OFFER and must never build —
       * the build-ref belongs to the `create_custom_page` build-chip resume flow.
       */
      forceOffer?: boolean;
      /**
       * Resolved search_filters artifact for a DATA-GROUNDED offer (the same
       * artifact the paired nav chip carries). Stored on the build chip as the
       * landing-filter fallback for the lazy build.
       */
      groundedFilters?: unknown;
    },
    emit?: ToolEmit
  ) => Promise<
    | { offered: true; messageId: string }
    | {
        built: true;
        ok: boolean;
        digests: ChartDataDigest[];
        expectedCount?: number;
      }
  >;
  updateCurrentPage: (
    input: { prompt: string },
    emit?: ToolEmit
  ) => Promise<{
    ok: boolean;
    digests: ChartDataDigest[];
    expectedCount?: number;
  }>;
  /**
   * Offer one or more reports to the user as clickable chips (it does NOT
   * launch the reports). Returns the resolved display names (order-preserving,
   * skipping any reportType that can't be resolved) so the model can name them
   * in its narration, plus the appended `report_offers` message id — the
   * `offer:report` stamp source for the narration's inline trigger(s).
   */
  offerReport: (input: {
    reportTypes: string[];
    prompt: string;
  }) => Promise<{ reportNames: string[]; messageId?: string }>;
  askUser: (input: { question: string; options?: string[] }) => void;
  /**
   * Append the offer tool's `narration` argument as the turn's assistant `text`
   * message. The drawer renders `text` messages through `parseOfferSegments`, so
   * the `[phrase](offer:nav|build)` markers in the narration become inline
   * clickable trigger spans — and `turnNarrationHasOffer` then suppresses the
   * now-redundant nav/build chip. Used by the offer handlers so the model's
   * user-facing offer text lives in a validated tool param, not freeform prose.
   */
  appendAssistantText: (
    text: string,
    offerTargets?: Partial<Record<OfferKind, string>>
  ) => void;
  /** Id of this turn's latest single_chart message (offer:page stamp source). */
  getLastStandardPageAnswerChartId: () => string | undefined;
};

export type ClientToolResult = { resultForModel: string; endsTurn?: boolean };

// Exported for the seeded-build landing narration (seeded-build-narration.ts),
// which seeds a narrate-only turn with the SAME digest contract lines the
// create_custom_page result uses — one phrasing, one grounding contract.
export function chartSummary(d: ChartDataDigest): string {
  if (d.error) {
    // The chart settled in an error phase — the data layer failed to load.
    // Narrate this as a FAILURE, never as "no data" (FE-2117).
    return `ERROR: couldn't load the "${d.title ?? d.metric}" chart — something went wrong rendering it. Do not claim there was no data; the chart failed to load.`;
  }
  const lines = [
    `chart: ${d.title ?? d.metric}`,
    `metric: ${d.metric}; rows: ${d.rowCount}; empty: ${d.empty}`,
  ];
  if (d.filtersApplied?.length) {
    // EXPLICIT ground truth that these filters resolved + applied, so the model
    // cannot infer "unfiltered" from `rows: 1` + the bare metric and invent
    // "the filter did not resolve" (FE-2117).
    lines.push(
      `filtersApplied: ${d.filtersApplied.join(', ')} (the latestValue already reflects these filters; do not claim a filter is unapplied)`
    );
  }
  if (d.resolvedRoleLabel) {
    // The role filter resolved to a CONCRETE node whose label can differ from
    // the user's own term (role ids are taxonomy-relative — "nurses" can
    // resolve to a different node under a custom taxonomy). Name the resolved
    // node, never parrot the request.
    lines.push(
      `resolvedRoleLabel: ${d.resolvedRoleLabel} (the role filter resolved to THIS exact role — narrate the figure using this label, not the user's phrasing, when they differ)`
    );
  }
  if (d.latestValue !== undefined) lines.push(`latestValue: ${d.latestValue}`);
  if (d.deltaPct !== undefined) lines.push(`deltaPct: ${d.deltaPct}`);
  if (d.firstDate !== undefined || d.lastDate !== undefined) {
    lines.push(`dates: ${d.firstDate ?? '?'} – ${d.lastDate ?? '?'}`);
  }
  if (d.topCategories?.length) {
    lines.push(
      `topCategories: ${d.topCategories.map((c) => `${c.label}=${c.value}`).join(', ')} (these are the ONLY entities/categories in this chart; narrate by these exact labels, not by what was requested)`
    );
  }
  if (d.bottomCategories?.length) {
    lines.push(
      `bottomCategories (LOWEST values, worst first): ${d.bottomCategories.map((c) => `${c.label}=${c.value}`).join(', ')} (the ranked worst end of the same breakdown — cite these exact labels and values when the question asks about problems, dissatisfaction, or why people leave)`
    );
  }
  return lines.join('\n');
}

function filterSummary(d: FilterResolutionDigest): string {
  const applied = d.applied
    .map((f) => `${f.requested} -> ${f.resolvedTo} (${f.filterId})`)
    .join('; ');
  const unresolved = d.unresolved
    .map((f) => `${f.requested}${f.reason ? ` (${f.reason})` : ''}`)
    .join('; ');
  return `FILTERS on ${d.targetPage}\nAPPLIED: ${applied || 'none'}\nUNRESOLVED: ${unresolved || 'none'}`;
}

/**
 * Build the model result for a single-value answer (NO chart card shown).
 * Shared by `render_chart`'s answerAsText branch and the `get_data_answer`
 * tool. `companionBlock` carries optional supporting-metric digests (empty for
 * the chart path, populated by get_data_answer); it renders between the figure
 * summary and the instructions.
 */
function answerAsTextResult(
  summary: string,
  standardPagePath: string | undefined,
  companionBlock = ''
): ClientToolResult {
  const standardPageLabel = standardPagePath
    ? toStandardPageLabel(standardPagePath)
    : undefined;
  // With a matching standard page, deliver the figure AND the page offer
  // through the validated offer_standard_page tool so the [](offer:page)
  // marker (hence the inline trigger) is enforced, not hoped-for.
  if (standardPageLabel) {
    return {
      resultForModel: `${summary}${companionBlock}\n\nENTITY CHECK FIRST: if the data above belongs to a DIFFERENT entity than the user asked about (e.g. filtersApplied is missing the asked company/geography/role and topCategories/title name some other company — the resolver failed and the fetch ran on defaults), do NOT present these figures at all: say you couldn't resolve <the asked entity> right now, suggest retrying, and END the turn. A default-company figure presented as the asked company's answer is a wrong answer (seen live: "sentiment at Tesla" answered with Workday's 0.51). Otherwise: this is a single-value answer — NO chart is shown to the user, and there is a matching standard page for it: ${standardPageLabel}. Do NOT reply with free text. Call offer_standard_page ONCE with a narration that: (1) LEADS with the asked figure in one grounded sentence (use latestValue / topCategories exactly; name only entities present in the data); (2) adds one or two supporting facts drawn ONLY from the data above — the trend between the dates shown, or a companion metric; express changes in rate metrics in percentage points, never relative percent; (3) ends with a SHORT, GENERIC reason to visit the page, ENDING on the marker [${standardPageLabel}](offer:page). Never bury the figure, never state a value not present above, never more than two supporting facts, never describe specific page contents beyond its name. Refer to each metric by its plain name ONLY — never explain what a metric includes or how it is calculated (no "including bonuses"-style glosses; you do not know the methodology).`,
    };
  }
  return {
    resultForModel: `${summary}${companionBlock}\n\nENTITY CHECK FIRST: if the data above belongs to a DIFFERENT entity than the user asked about (resolver failure — the fetch ran on defaults), do NOT present these figures: say you couldn't resolve the asked entity right now and END the turn. Otherwise: this is a single-value answer — NO chart is shown to the user. Reply with ONE short paragraph (2–3 sentences): LEAD with the asked figure (use latestValue / topCategories exactly; name only entities present in the data), then one or two supporting facts drawn ONLY from the data above (the trend between the dates shown, or a companion metric; percentage points for rate changes, never relative percent), then ONE short suggested follow-up grounded in a metric you can actually show (e.g. the growth trend, or a breakdown). Never bury the figure; never state a value not present above; never more than two supporting facts. Refer to each metric by its plain name ONLY — never explain what a metric includes or how it is calculated (no "including bonuses"-style glosses; you do not know the methodology). Do NOT refer to "the chart". Then end your turn.`,
  };
}

export async function executeClientTool(
  name: string,
  input: unknown,
  artifacts: ArtifactCache,
  deps: ClientToolDeps,
  // Optional progress sink. Tools that don't emit ignore it — behaviour is
  // unchanged. Consumed by create_custom_page to stream build progress.
  emit?: ToolEmit
): Promise<ClientToolResult> {
  // Dev-only eval recording around the single dispatch point — covers every
  // loop path (main turn + report ride-along resume). No-op in production.
  const evalRecord = evalRecordToolCall(name, input);
  try {
    const result = await executeClientToolInner(
      name,
      input,
      artifacts,
      deps,
      emit
    );
    evalRecordToolResult(evalRecord, result);
    return result;
  } catch (err) {
    evalRecordToolThrow(evalRecord);
    throw err;
  }
}

async function executeClientToolInner(
  name: string,
  input: unknown,
  artifacts: ArtifactCache,
  deps: ClientToolDeps,
  emit?: ToolEmit
): Promise<ClientToolResult> {
  switch (name) {
    case 'navigate': {
      const { optionId, narration, filterArtifactId } = input as {
        optionId: string;
        narration: string;
        filterArtifactId?: string;
      };
      // Terminal tool: NEVER drive the router here. The hard rule is that the
      // assistant only navigates after an explicit user click, so we present a
      // confirmation chip and end the turn. The chip's click handler performs
      // the guarded navigation (and resumes any chained task).
      //
      // Resolve the confirmation FIRST: on an unknown page nothing is rendered,
      // so a narration whose `[phrase](offer:nav)` marker has no backing chip
      // can never show up as a dead offer.
      //
      // RESOLVE-FIRST compound asks ("open X and filter to Y"): the model
      // resolves the filters up front (search_filters target 'chart') and
      // passes the artifact here; the click then navigates AND applies that
      // exact slice — one landing, already filtered. The continuation resume
      // is suppressed for grounded navs: the filter half WAS the chained task,
      // and a resumed turn would re-answer the compound prompt on the
      // destination (double-apply loop risk).
      const navGrounded = filterArtifactId
        ? groundedArtifactOrUndefined(artifacts.get(filterArtifactId))
        : undefined;
      const result = deps.requestNavigateConfirm({
        optionId,
        // No entityType here: the navigate tool carries none, and the click
        // path falls back to inferring the anchor from the destination path
        // (navigateToMatch's resolvedEntityType ?? path-inferred entityType).
        ...(navGrounded !== undefined
          ? { withContinuation: false, resolvedFilters: navGrounded }
          : {}),
      });
      if ('unknownPage' in result) {
        // Not a real page — non-terminal error so the model can recover and
        // never claims a phantom navigation (FE-2117).
        return {
          resultForModel: `ERROR: ${optionId} is not a known page; do not claim to have navigated.`,
        };
      }
      // Render the param `narration` as the assistant text. On EVERY page its
      // `[phrase](offer:nav)` marker becomes an inline trigger and suppresses
      // the redundant nav-confirm chip; a markerless narration falls back to the
      // chip. Nothing auto-navigates — home behaves exactly like off-home now.
      if (narration) {
        deps.appendAssistantText(narration, { nav: result.messageId });
      }
      return {
        resultForModel: `Presented a confirmation to open ${result.pageName}; the user must click to navigate. Tell the user you can take them to ${result.pageName} and to click the button to confirm. Do not claim to have navigated.`,
        endsTurn: true,
      };
    }
    case 'apply_filters': {
      const { filterArtifactId, target, merge } = input as {
        filterArtifactId: string;
        target: string;
        merge?: boolean;
      };
      const filterResponse = artifacts.get(filterArtifactId);
      if (filterResponse === undefined) {
        return {
          resultForModel: `ERROR: no filter artifact (${filterArtifactId}) — call search_filters first to resolve filters, then apply_filters.`,
        };
      }
      const digest = await deps.applyFilters({ filterResponse, target, merge });
      // Optimistic apply (DP4): the command is enqueued, not confirmed — keep
      // the narration to what the artifact proves and present-progressive.
      return {
        resultForModel: `${filterSummary(digest)}\n\nThe filters are being applied to the page now (they land within a moment) — say you are applying them, and describe WHAT was applied from the APPLIED list (and call out anything UNRESOLVED). Do not claim to be waiting, and do not describe the page's resulting data (you have not seen it).`,
      };
    }
    case 'get_data_answer': {
      const { filterArtifactId, metric, entityType } = input as {
        filterArtifactId: string;
        metric: keyof typeof DIRECT_ANSWER_CONFIGS;
        entityType: string;
      };
      const filterResponse = artifacts.get(filterArtifactId);
      if (filterResponse === undefined) {
        return {
          resultForModel: `ERROR: no filter artifact (${filterArtifactId}) — call search_filters with target 'chart' first, then get_data_answer with its filterArtifactId.`,
        };
      }
      const canned = DIRECT_ANSWER_CONFIGS[metric];
      if (!canned) {
        return {
          resultForModel: `ERROR: unsupported metric "${metric}" for a direct answer. Do not retry this tool for it; answer via the chart flow (generate_chart_config) instead.`,
        };
      }
      // domain is canned per-metric; entityType is a runtime input, so the
      // standard-page offer path is assembled here (see direct-answer-configs).
      const standardPagePath = `/${entityType}/${canned.domain}`;
      // Deterministic single-value fetch (DP3, headless): the canned config
      // replaces the generate_chart_config LLM call; the search_filters
      // artifact rides the SAME isolated-filter-scope pipeline every inline
      // chart uses — inside the offscreen digest host. Primary AND companion
      // metrics fetch fully in parallel; nothing touches the transcript, so a
      // failed primary can't strand phantom companion messages.
      const t0 = performance.now();
      const primaryPromise = deps.fetchChartDigest({
        config: canned.config,
        filters: filterResponse,
        entityType,
        intentSummary: `direct ${metric} answer`,
      });
      const companionsPromise = Promise.all(
        (DIRECT_ANSWER_COMPANIONS[metric] ?? []).map((companion) =>
          deps
            .fetchChartDigest({
              config: DIRECT_ANSWER_CONFIGS[companion].config,
              filters: filterResponse,
              entityType,
              intentSummary: `companion ${companion} context`,
            })
            .then((d) => ({ companion, digest: d }))
            .catch(() => undefined)
        )
      );
      const digest = await primaryPromise;
      evalRecordDigest('get_data_answer', String(metric), digest);
      const tPrimary = performance.now();
      const summary = chartSummary(digest);
      if (summary.startsWith('ERROR')) {
        // Honest failure with a recovery path — the generic guidance-less ERROR
        // is what made the old chart path loop on count questions. In-flight
        // companions resolve into the void (no transcript side effects).
        return {
          resultForModel: `${summary}\nDo not invent a number and do not retry this tool with the same artifact. Tell the user plainly the figure could not be loaded right now and end the turn.`,
        };
      }
      // Offer wiring: the card-less context message the drawer's offer:page
      // inline link reads (standardPagePath + filters + prompt). Success only.
      deps.appendAnswerContextChart({
        config: canned.config,
        filters: filterResponse,
        entityType,
        intentSummary: `direct ${metric} answer`,
        standardPagePath,
      });
      // Companion metrics (SAME filters) for answer context (CEO "fuller
      // answers" mandate). A failed/empty companion drops silently and must
      // never fail the answer.
      const companionResults = (await companionsPromise).filter(
        (c): c is NonNullable<typeof c> =>
          c !== undefined && !c.digest.error && !c.digest.empty
      );
      if (process.env.NODE_ENV === 'development') {
        const tCompanions = performance.now();
        console.info('[ga-latency] get_data_answer', {
          metric,
          primaryMs: Math.round(tPrimary - t0),
          totalMs: Math.round(tCompanions - t0),
        });
      }
      const companionBlock = companionResults.length
        ? `\n\ncompanion metrics (context, SAME filters as the answer — optional supporting facts, use at most one or two):\n${companionResults
            .map((c) => `--- ${c.companion} ---\n${chartSummary(c.digest)}`)
            .join('\n')}`
        : '';
      // Thread the metric's unit/formatting note so the model states the figure
      // correctly (currency vs rate vs count vs score). Only on the SUCCESS
      // path — the ERROR branch above needs no unit hint.
      const summaryWithUnit = `${summary}\nunitHint: ${canned.unitHint}`;
      return answerAsTextResult(
        summaryWithUnit,
        standardPagePath,
        companionBlock
      );
    }
    case 'get_data_breakdown': {
      const { filterArtifactId, breakdown, entityType } = input as {
        filterArtifactId: string;
        breakdown: keyof typeof BREAKDOWN_ANSWER_CONFIGS;
        entityType: string;
      };
      const filterResponse = artifacts.get(filterArtifactId);
      if (filterResponse === undefined) {
        return {
          resultForModel: `ERROR: no filter artifact (${filterArtifactId}) — call search_filters with target 'chart' first, then get_data_breakdown with its filterArtifactId.`,
        };
      }
      const canned = BREAKDOWN_ANSWER_CONFIGS[breakdown];
      if (!canned) {
        return {
          resultForModel: `ERROR: unsupported breakdown "${breakdown}". Do not retry this tool for it; route the question to the matching page instead.`,
        };
      }
      const standardPagePath = `/${entityType}/${canned.domain}`;
      // Same deterministic headless pattern as get_data_answer: ranked
      // breakdown + outcome companions fetch fully in parallel; no transcript
      // side effects until success.
      const breakdownPromise = deps.fetchChartDigest({
        config: canned.config,
        filters: filterResponse,
        entityType,
        intentSummary: `${breakdown} breakdown`,
      });
      const breakdownCompanionsPromise = Promise.all(
        (BREAKDOWN_ANSWER_COMPANIONS[breakdown] ?? []).map((companion) =>
          deps
            .fetchChartDigest({
              config:
                DIRECT_ANSWER_CONFIGS[
                  companion as keyof typeof DIRECT_ANSWER_CONFIGS
                ].config,
              filters: filterResponse,
              entityType,
              intentSummary: `companion ${companion} context`,
            })
            .then((d) => ({ companion, digest: d }))
            .catch(() => undefined)
        )
      );
      const breakdownDigest = await breakdownPromise;
      evalRecordDigest('get_data_breakdown', breakdown, breakdownDigest);
      if (
        breakdownDigest.error ||
        breakdownDigest.empty ||
        !breakdownDigest.topCategories?.length
      ) {
        // Honest failure with the same no-invention contract as
        // get_data_answer; in-flight companions resolve into the void.
        return {
          resultForModel: `ERROR: the ${breakdown} breakdown could not be loaded right now (no ranked categories returned). Do not invent aspect names or figures and do not retry this tool with the same artifact more than once. Fall back to offering the matching page without figures.`,
        };
      }
      // offer:page wiring — the answer's link lands the user on the domain
      // page with the SAME resolved filters (deterministic text = link).
      deps.appendAnswerContextChart({
        config: canned.config,
        filters: filterResponse,
        entityType,
        intentSummary: `${breakdown} breakdown`,
        standardPagePath,
      });
      const breakdownCompanions = (await breakdownCompanionsPromise).filter(
        (c): c is NonNullable<typeof c> =>
          c !== undefined && !c.digest.error && !c.digest.empty
      );
      const companionBlock = breakdownCompanions.length
        ? `\n\ncompanion metrics (context, SAME filters — outcome figures to pair with the breakdown):\n${breakdownCompanions
            .map((c) => `--- ${c.companion} ---\n${chartSummary(c.digest)}`)
            .join('\n')}`
        : '';
      const high = (breakdownDigest.topCategories ?? [])
        .map((c) => `${c.label}=${c.value}`)
        .join(', ');
      const low = (breakdownDigest.bottomCategories ?? [])
        .map((c) => `${c.label}=${c.value}`)
        .join(', ');
      return {
        resultForModel: `RANKED BREAKDOWN (${breakdown}; ${breakdownDigest.rowCount} categories total; unitHint: ${canned.unitHint})\n${canned.highEndLabel}: ${high}\n${canned.lowEndLabel}: ${low || '(not returned)'}${
          breakdownDigest.filtersApplied?.length
            ? `\nfiltersApplied: ${breakdownDigest.filtersApplied.join(', ')} (the values already reflect these filters)`
            : ''
        }${companionBlock}\n\nYou now have the ranked data — do NOT call get_data_breakdown (or any data tool) again this turn; your only remaining action is the offer. Pick the END that answers the QUESTION and cite 2–3 categories — EACH stated as "<aspect> at <its exact score>" (e.g. "work-life balance at -0.28"): naming an aspect WITHOUT its numeric score is NOT grounding and is not allowed. Never list the full set. Routing of the end: leaving/attrition/dissatisfaction/problems → the ${canned.lowEndLabel}; loved/best/strengths → the ${canned.highEndLabel}; an overall judgement ask → one or two from each end plus a companion figure. The GLOBAL CAUSAL-CLAIM BAN applies: these are the lowest/highest-rated signals, NOT proven causes — say "the lowest of their experience signals", never "because of" or "driven by". Deliver the answer through the offer tool this ask's routing requires — a causal/why ask folds the cited scores into offer_page_or_custom's narration (page + build options); a direct "what do employees love/hate" ask uses offer_standard_page with a narration that leads with the cited scores and ends on the [${toStandardPageLabel(standardPagePath) ?? 'page'}](offer:page) marker. Do NOT reply in free text.`,
      };
    }
    case 'render_chart': {
      const { configArtifactId } = input as { configArtifactId: string };
      const payload = artifacts.get(configArtifactId) as
        | {
            config: unknown;
            filters?: unknown;
            entityType?: string;
            intentSummary?: string;
            chartSubtitle?: string;
            standardPagePath?: string;
            answerAsText?: boolean;
          }
        | undefined;
      if (!payload) {
        return {
          resultForModel: `ERROR: chart config artifact ${configArtifactId} not found`,
        };
      }
      // Single-value answer (DP3, headless): NO chart card is shown to the
      // user — the digest comes from the offscreen host, and the model must
      // deliver the figure as plain prose, not narrate "the chart".
      if (payload.answerAsText) {
        const answerDigest = await deps.fetchChartDigest({
          config: payload.config,
          filters: payload.filters,
          entityType: payload.entityType,
          intentSummary: payload.intentSummary,
          chartSubtitle: payload.chartSubtitle,
        });
        const answerSummary = chartSummary(answerDigest);
        // Same error-first contract as the visible path below.
        if (answerSummary.startsWith('ERROR')) {
          return { resultForModel: answerSummary };
        }
        if (payload.standardPagePath) {
          deps.appendAnswerContextChart({
            config: payload.config,
            filters: payload.filters,
            entityType: payload.entityType,
            intentSummary: payload.intentSummary,
            standardPagePath: payload.standardPagePath,
          });
        }
        return answerAsTextResult(answerSummary, payload.standardPagePath);
      }
      const digest = await deps.renderChart(payload);
      evalRecordDigest('render_chart', digest.metric, digest);
      const summary = chartSummary(digest);
      // A failed render already reads as ERROR — never tell the model the chart
      // is displayed when it is not.
      if (summary.startsWith('ERROR')) {
        return { resultForModel: summary };
      }
      // The digest above describes the DATA, not that the action is finished.
      // Without an explicit completion signal, an agentic model (e.g.
      // claude-sonnet-4) reads "here is the chart data" as "now produce the
      // chart" and calls generate_chart_config/render_chart again, double-
      // rendering the same chart. State plainly that it is now on screen and the
      // request is fulfilled so the model narrates and stops (FE-2117).
      //
      // Advertise the post-chart follow-up markers the model MAY use for THIS
      // chart, computed with the SAME predicates the drawer chips use so the
      // model never offers a marker whose chip/trigger cannot render. Client
      // tools run on the FE, so these checks match the render-time gating.
      const standardPageLabel = payload.standardPagePath
        ? toStandardPageLabel(payload.standardPagePath)
        : undefined;
      // Each entry describes an option and names the marker scheme to wrap it
      // in — deliberately NOT written marker-first, so the examples don't fight
      // the reason-first instruction below.
      const followUps: string[] = [];
      if (standardPageLabel) {
        followUps.push(
          `offer:page — open ${standardPageLabel} (shows this question's data with its filters applied); wrap the page name: [${standardPageLabel}](offer:page)`
        );
      }
      const followUpInstruction = followUps.length
        ? `\n\nAfter the grounded sentence, END your narration by offering the applicable follow-ups below as inline links: a short generic reason FIRST, then the clickable phrase wrapped in the named marker as the LAST thing in the clause (e.g. "the full picture lives on one page — [${standardPageLabel ?? 'Company Compositions'}](offer:page)"). Use ONLY these marker schemes, do not invent others:\n- ${followUps.join('\n- ')}`
        : '';
      return {
        resultForModel: `${summary}\n\nThe chart is now displayed to the user — this fulfils the request for it. Do NOT generate or render this same chart again. Narrate ONLY what the data above actually shows: name only the entities/categories present in this chart (per its title, latestValue, and topCategories) and the figures shown. Do NOT name any company, geography, role, or value that is not in the data above, even if the user's request named it — if the chart shows different or fewer entities than asked (e.g. peer companies or a region rollup instead of the exact items requested), describe what is actually on screen rather than restating the request. Give a brief grounded sentence, then end your turn; call offer_report LAST only if a prebuilt report clearly matches, and render another chart only if the user asked for a different one.${followUpInstruction}`,
      };
    }
    case 'create_custom_page': {
      const { prompt, entityType, narration } = input as {
        prompt: string;
        entityType: string;
        narration?: string;
      };
      const result = await deps.requestCreateCustomPage(
        { prompt, entityType },
        emit
      );
      if ('offered' in result) {
        // OFFER mode (default): render the param `narration` as the assistant
        // text so its `[phrase](offer:build)` marker becomes an inline trigger
        // (and `turnNarrationHasOffer` suppresses the redundant build-confirm
        // chip), appended BEFORE the chip so the offer text reads above the
        // button. Only append here — in BUILD-HERE mode the build already ran,
        // so a marker-bearing narration would leave a stray "build" trigger for
        // a dashboard that is already being built.
        if (narration) {
          deps.appendAssistantText(narration, { build: result.messageId });
        }
        // Present the build-confirm chip and end the turn. Clicking the chip
        // navigates to an empty draft and resumes the turn in BUILD-HERE mode
        // (below), so the loop never builds on this terminal offer.
        return {
          resultForModel:
            'Presented a "Build custom dashboard" button; the user must click it to build and open the dashboard. Tell the user you can put that dashboard together and to click the button to build it. Do NOT claim the page was built or opened.',
          endsTurn: true,
        };
      }
      // BUILD-HERE mode: the user confirmed; the CREATE page-gen ran on the
      // current draft. Narrate ONLY the actual widgets the build collected.
      if (!result.ok) {
        return {
          resultForModel:
            'ERROR: the dashboard build did not confirm in time; it may be incomplete — do not claim the dashboard was built.',
        };
      }
      const { digests, expectedCount } = result;
      if (digests.length === 0) {
        // The build confirmed but the page's widget data hasn't settled yet, so
        // we have nothing concrete to ground on — do not invent specific charts.
        return {
          resultForModel:
            'The dashboard was created, but no chart data has settled yet — do not describe specific charts or claim the page covers any particular metric; just say the dashboard is being built.',
        };
      }
      // Partial coverage (a collector tier fired before every widget settled):
      // the absolute "ONLY charts on the page" contract would be wrong-by-
      // omission — a chart that exists but hasn't settled would be denied.
      // Tell the model exactly what settled and what's still loading (DP5).
      if (expectedCount !== undefined && digests.length < expectedCount) {
        return {
          resultForModel: `Created the dashboard with ${expectedCount} charts; data has settled for ${digests.length} of them so far (the rest are still loading):\n${digests.map(chartSummary).join('\n---\n')}\n\nGive a brief grounded summary (1–2 sentences) leading with the concrete figures above, referring to charts by their titles, and mention the remaining charts are still loading. Do NOT invent contents for charts not listed — never name their metrics or values. Frame it as a NEWLY BUILT dashboard, not an update. This fulfils the request; then end your turn — call offer_report LAST only if a prebuilt report clearly matches.`,
        };
      }
      const builtWidgetLabel =
        digests.length === 1 ? '1 widget' : `${digests.length} widgets`;
      // Enumerate the ACTUAL widgets the build produced and constrain the
      // narration to them — CREATE semantics ("created", not "updated"). Without
      // this, the model narrates a generic "comprehensive workforce dashboard"
      // template and invents sections that aren't on the page (FE-2117).
      return {
        resultForModel: `Created the dashboard with ${builtWidgetLabel}. These are the ONLY charts on the page — narrate ONLY these:\n${digests.map(chartSummary).join('\n---\n')}\n\nGive a brief grounded summary (1–2 sentences) that refers to these charts by their titles and leads with the concrete figures above. Do NOT mention any metric, theme, or section that is not one of the charts listed — e.g. do not claim the dashboard covers compensation, sentiment, demographics, attrition, or transitions unless a chart above is actually about it. Frame it as a NEWLY BUILT dashboard, not an update. This fulfils the request; then end your turn — call offer_report LAST only if a prebuilt report clearly matches.`,
      };
    }
    case 'offer_page_or_custom': {
      // Partial-fit "offer both": present the templated PAGE first (primary
      // offer), then a secondary build-a-custom-dashboard chip. Order matters —
      // the page is the primary answer, the custom build is the deeper fallback.
      const { optionId, prompt, entityType, narration, filterArtifactId } =
        input as {
          optionId: string;
          prompt: string;
          entityType: string;
          narration?: string;
          filterArtifactId?: string;
        };
      // DATA-GROUNDED offer: the model resolves the ask's entity scope up
      // front (and on causal flows passes the SAME artifact its figures were
      // fetched with), so the page link applies that exact slice on the
      // destination — "hiring patterns at Ford" must land on a page filtered
      // to Ford, never the page's default primary (observed live 2026-07-08:
      // Role Sentiment landing on the default role). A missing/unknown/EMPTY
      // artifact degrades to the pure navigation this offer always was.
      const groundedFilters = filterArtifactId
        ? groundedArtifactOrUndefined(artifacts.get(filterArtifactId))
        : undefined;
      // Page chip FIRST — and BEFORE the narration renders, so an unresolved
      // page id downgrades the narration's nav marker instead of leaving a dead
      // `[phrase](offer:nav)` trigger with no backing chip.
      // withContinuation:false — it is a terminal "land me on the page" action;
      // replaying this (often causal) prompt on the page would drive a
      // search/apply filter loop. The build chip owns the deeper analysis.
      const navResult = deps.requestNavigateConfirm({
        optionId,
        withContinuation: false,
        ...(groundedFilters !== undefined
          ? { resolvedFilters: groundedFilters, entityType }
          : {}),
      });
      if ('unknownPage' in navResult) {
        // Nav id did not resolve (should not happen — optionId is enum-bound to
        // candidate ids); only the build offer stands. Render the narration with
        // its nav marker downgraded to plain text, then still offer the build so
        // the turn isn't a dead end.
        const buildResult = await deps.requestCreateCustomPage(
          {
            prompt,
            entityType,
            forceOffer: true,
            ...(groundedFilters !== undefined ? { groundedFilters } : {}),
          },
          emit
        );
        if (narration) {
          deps.appendAssistantText(
            stripOfferMarkers(narration, 'nav'),
            'offered' in buildResult
              ? { build: buildResult.messageId }
              : undefined
          );
        }
        return {
          resultForModel:
            'Presented a "Build custom dashboard" button (the templated page id did not resolve). Tell the user you can build that dashboard and to click the button; do NOT claim any page was opened or built.',
          endsTurn: true,
        };
      }
      // Secondary: the build-a-dashboard chip. forceOffer:true — offer_page_or
      // _custom is a pure OFFER and must NEVER build: the runner hard-routes
      // forceOffer to the OFFER path (clearing any stale pending-build ref), so
      // this always resolves `{ offered: true }`. The grounded artifact rides
      // along so the lazy build's landing can fall back to it when the
      // page-gen's own filter resolution fails.
      const buildResult = await deps.requestCreateCustomPage(
        {
          prompt,
          entityType,
          forceOffer: true,
          ...(groundedFilters !== undefined ? { groundedFilters } : {}),
        },
        emit
      );
      // Narration LAST so both chips exist to stamp. Both markers are schema-
      // required, so both chips are suppressed by their stamps and the inline
      // triggers are the only visible affordances.
      if (narration) {
        deps.appendAssistantText(narration, {
          nav: navResult.messageId,
          ...('offered' in buildResult ? { build: buildResult.messageId } : {}),
        });
      }
      return {
        resultForModel: `Presented BOTH options: a button to open ${navResult.pageName}, and a "Build custom dashboard" button. Tell the user the ${navResult.pageName} page covers the core of their question, and that you can also build a custom dashboard for the full request — they click whichever they want. Do NOT claim either was opened or built.`,
        endsTurn: true,
      };
    }
    case 'offer_standard_page': {
      const { narration } = input as { narration: string };
      // The single-value figure AND the page offer both live in the validated
      // narration. Render it as the assistant text so its [phrase](offer:page)
      // marker becomes an inline trigger, stamped with the id of the single-
      // value answer chart it describes (the drawer wires the click to that
      // chart's standard page + filters). The stamp source is that specific
      // chart, not "the latest chart", so an ordinary chart rendered later in
      // the turn can't retarget the offer. No such chart → no stamp → the
      // marker renders as plain text and the "Go to <page>" button stands.
      const chartMessageId = deps.getLastStandardPageAnswerChartId();
      deps.appendAssistantText(
        narration,
        chartMessageId ? { page: chartMessageId } : undefined
      );
      return {
        resultForModel:
          'Presented the single-value answer with an inline link to the standard page; the user clicks the link to open it. Do NOT add more text or navigate — end your turn.',
        endsTurn: true,
      };
    }
    case 'update_current_page': {
      const { ok, digests, expectedCount } = await deps.updateCurrentPage(
        input as { prompt: string },
        emit
      );
      if (!ok) {
        return {
          resultForModel:
            'ERROR: the page update did not confirm in time; it may be incomplete — do not claim the page was updated.',
        };
      }
      // The command confirmed but the page's widget data hasn't settled yet, so
      // we have nothing concrete to ground on — do not invent specific charts.
      if (digests.length === 0) {
        return {
          resultForModel:
            'The page was updated, but no chart data has settled yet — do not describe specific charts or claim the page covers any particular metric; just say the page is being built.',
        };
      }
      // Partial coverage (a collector tier fired before every widget settled):
      // the absolute "ONLY charts on the page" contract would be wrong-by-
      // omission — a chart that exists but hasn't settled would be denied.
      // Tell the model exactly what settled and what's still loading (DP5).
      if (expectedCount !== undefined && digests.length < expectedCount) {
        return {
          resultForModel: `The page has ${expectedCount} charts; data has settled for ${digests.length} of them so far (the rest are still loading):\n${digests.map(chartSummary).join('\n---\n')}\n\nGive a brief grounded summary (1–2 sentences) leading with the concrete figures above, referring to charts by their titles, and mention the remaining charts are still loading. Do NOT invent contents for charts not listed — never name their metrics or values. This fulfils the request; then end your turn — call offer_report LAST only if a prebuilt report clearly matches.`,
        };
      }
      const widgetLabel =
        digests.length === 1 ? '1 widget' : `${digests.length} widgets`;
      // Enumerate the ACTUAL widgets now on the page and constrain the narration
      // to them. Without this, the model narrates a generic "comprehensive
      // workforce dashboard" template and invents sections that aren't on the
      // page (e.g. claiming compensation/sentiment/demographics charts that were
      // never generated) — the page-build analogue of the single-chart
      // guardrail above (FE-2117).
      return {
        resultForModel: `The page now has ${widgetLabel}. These are the ONLY charts on the page — narrate ONLY these:\n${digests.map(chartSummary).join('\n---\n')}\n\nGive a brief grounded summary (1–2 sentences) that refers to these charts by their titles and leads with the concrete figures above. Do NOT mention any metric, theme, or section that is not one of the charts listed — e.g. do not claim the page covers compensation, sentiment, demographics, attrition, or transitions unless a chart above is actually about it. This fulfils the request; then end your turn — call offer_report LAST only if a prebuilt report clearly matches.`,
      };
    }
    case 'offer_report': {
      const { reportTypes, prompt, narration } = input as {
        reportTypes: string[];
        prompt: string;
        narration?: string;
      };
      const result = await deps.offerReport({ reportTypes, prompt });
      // Zero chips: ending the turn would dead-end with nothing shown — return
      // a non-terminal result and let the model recover/continue (FE-2117).
      if (result.reportNames.length === 0) {
        return { resultForModel: 'No matching report could be offered.' };
      }
      // Render the validated `narration` as the assistant text, stamped with
      // the offers message id so its [Report Name](offer:report) marker(s)
      // become inline triggers wired to the SAME open-report-builder action as
      // the cards (which the stamp then suppresses). Sibling pattern:
      // offer_standard_page above.
      if (narration) {
        deps.appendAssistantText(
          narration,
          result.messageId ? { report: result.messageId } : undefined
        );
      }
      return {
        resultForModel: `Offered ${result.reportNames.length} report(s): ${result.reportNames.join(', ')} — the explanation with the inline link(s) is already shown to the user. Do NOT repeat or re-describe the report(s); end your turn.`,
        endsTurn: true,
      };
    }
    case 'ask_user': {
      deps.askUser(input as { question: string; options?: string[] });
      return { resultForModel: 'asked user; awaiting reply', endsTurn: true };
    }
    default:
      return { resultForModel: `ERROR: ${name} is not a client tool` };
  }
}
